import type { GpuContext } from '../context.js';
import { workgroupCount } from '../context.js';
import type { GpuImage } from '../resources.js';
import { Struct } from '../uniform-writer.js';

export type Rgb = readonly [number, number, number];

export interface QuantizeOptions {
  /** Ordered-dither amplitude; 0 keeps hard bands. */
  dither?: number;
  /** Measure colour distance in linear light (closer to perceptual). */
  linear?: boolean;
}

/**
 * Snaps colours to a palette.
 *
 * A print-production step: separations are priced per ink, and upscaling turns a
 * 6-colour design into thousands of near-identical shades. Quantising also
 * removes the JPEG ringing every marketplace screenshot carries.
 */
export function quantizePalette(
  ctx: GpuContext,
  src: GpuImage,
  dst: GpuImage,
  palette: readonly Rgb[],
  options: QuantizeOptions = {},
): void {
  const count = Math.min(palette.length, 256);
  const data = new Float32Array(Math.max(1, count) * 4);
  for (let i = 0; i < count; i++) {
    const entry = palette[i]!;
    data[i * 4] = entry[0];
    data[i * 4 + 1] = entry[1];
    data[i * 4 + 2] = entry[2];
    data[i * 4 + 3] = 1;
  }

  const buffer = ctx.storageBuffer(data.byteLength, 0, 'palette');
  ctx.queue.writeBuffer(buffer, 0, data);

  ctx.dispatch(
    {
      module: 'quantize.wgsl',
      defines: { DST_FORMAT: dst.format },
      bindings: [
        { kind: 'texture', format: src.format },
        { kind: 'buffer', access: 'read-only' },
        { kind: 'storageTexture', format: dst.format },
        { kind: 'uniform' },
      ],
    },
    [
      src,
      buffer,
      dst,
      ctx.uniform(
        new Struct(32)
          .i(0, dst.width, dst.height)
          .u(8, count)
          .f(12, options.dither ?? 0)
          .u(16, options.linear === false ? 0 : 1, 0, 0, 0).bytes,
      ),
    ],
    [workgroupCount(dst.width, 8), workgroupCount(dst.height, 8)],
    `quantize k=${count}`,
  );

  ctx.flush();
  ctx.releaseBuffer(buffer);
}

/**
 * k-means over a downsampled readback of the image.
 *
 * Runs on the CPU on purpose: it operates on a few thousand samples, converges
 * in a handful of iterations, and the result is a tiny palette rather than pixel
 * data - a GPU implementation would cost more in synchronisation than it saves.
 * Only pixels with meaningful alpha vote, so the transparent background cannot
 * claim one of the k slots.
 */
export function kmeansPalette(
  pixels: Uint8Array | Uint8ClampedArray,
  k: number,
  options: { alphaThreshold?: number; iterations?: number; seed?: number } = {},
): Rgb[] {
  const alphaThreshold = (options.alphaThreshold ?? 0.5) * 255;
  const iterations = options.iterations ?? 12;

  const samples: number[] = [];
  for (let i = 0; i < pixels.length; i += 4) {
    if (pixels[i + 3]! >= alphaThreshold) {
      samples.push(pixels[i]! / 255, pixels[i + 1]! / 255, pixels[i + 2]! / 255);
    }
  }
  const sampleCount = samples.length / 3;
  if (sampleCount === 0) return [];
  const clusters = Math.max(1, Math.min(k, sampleCount));

  // Deterministic strided seeding beats random init here: designs have large
  // flat areas, so evenly spaced samples already cover the distinct inks.
  const centroids = new Float32Array(clusters * 3);
  for (let c = 0; c < clusters; c++) {
    const index = Math.floor(((c + 0.5) / clusters) * sampleCount);
    centroids[c * 3] = samples[index * 3]!;
    centroids[c * 3 + 1] = samples[index * 3 + 1]!;
    centroids[c * 3 + 2] = samples[index * 3 + 2]!;
  }

  const sums = new Float64Array(clusters * 3);
  const counts = new Uint32Array(clusters);
  for (let iteration = 0; iteration < iterations; iteration++) {
    sums.fill(0);
    counts.fill(0);
    for (let s = 0; s < sampleCount; s++) {
      const r = samples[s * 3]!;
      const g = samples[s * 3 + 1]!;
      const b = samples[s * 3 + 2]!;
      let best = 0;
      let bestDistance = Infinity;
      for (let c = 0; c < clusters; c++) {
        const dr = r - centroids[c * 3]!;
        const dg = g - centroids[c * 3 + 1]!;
        const db = b - centroids[c * 3 + 2]!;
        const distance = dr * dr + dg * dg + db * db;
        if (distance < bestDistance) {
          bestDistance = distance;
          best = c;
        }
      }
      sums[best * 3] = sums[best * 3]! + r;
      sums[best * 3 + 1] = sums[best * 3 + 1]! + g;
      sums[best * 3 + 2] = sums[best * 3 + 2]! + b;
      counts[best] = counts[best]! + 1;
    }
    let moved = 0;
    for (let c = 0; c < clusters; c++) {
      if (counts[c] === 0) continue;
      const nr = sums[c * 3]! / counts[c]!;
      const ng = sums[c * 3 + 1]! / counts[c]!;
      const nb = sums[c * 3 + 2]! / counts[c]!;
      moved += Math.abs(nr - centroids[c * 3]!) + Math.abs(ng - centroids[c * 3 + 1]!) + Math.abs(nb - centroids[c * 3 + 2]!);
      centroids[c * 3] = nr;
      centroids[c * 3 + 1] = ng;
      centroids[c * 3 + 2] = nb;
    }
    if (moved < 1e-4) break;
  }

  const palette: Rgb[] = [];
  for (let c = 0; c < clusters; c++) {
    if (counts[c] === 0 && iterations > 0) continue; // Drop unused slots.
    palette.push([centroids[c * 3]!, centroids[c * 3 + 1]!, centroids[c * 3 + 2]!]);
  }
  return palette;
}
